import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { Overlay, Card, Grid } from '@enterprise-ui/canvas-ui-react';
import React, { useRef, useReducer, useEffect } from 'react';
import CalendarDates from './components/CalendarDates.js';
import InputField from './components/InputField.js';
import NavHeader from './components/NavHeader.js';
import QuickRangeSelection from './components/QuickRangeSelection.js';
import { useKeyPress, determineErrorState, toActiveMonth, handleDateTransition, getDate } from './utils/gregorianUtility.js';
import { reducer, setSelectedRange, setSelectedDate, setDisabledDates, hideCalendar, setCalendarVisibility, setActiveMonth } from './utils/reducer.js';

var _excluded = ["autoOk", "className", "data-testid", "disabled", "disableDates", "error", "errorText", "format", "highlight", "hintText", "id", "noValidate", "limitRange", "location", "onChange", "onError", "onUpdate", "quickSelectRange", "required", "type", "yearRange", "value"];
var initialErrorState = {
  error: false,
  errorText: ''
};
var initialMonthArr = [];
var createInitialState = function createInitialState(_ref) {
  var value = _ref.value,
    disableDates = _ref.disableDates,
    format = _ref.format,
    isGregorianRange = _ref.isGregorianRange,
    yearRange = _ref.yearRange;
  if (isGregorianRange) {
    var _value$endDate, _value$endDate2, _value$startDate, _value$startDate2;
    var errorState = determineErrorState({
      dateIn: value === null || value === void 0 ? void 0 : value.startDate,
      dateRange: true,
      disableDates: disableDates,
      format: format
    }) && determineErrorState({
      dateIn: value === null || value === void 0 ? void 0 : value.endDate,
      dateRange: true,
      disableDates: disableDates,
      format: format
    });
    return {
      activeMonth: '',
      disableDates: disableDates,
      internalError: errorState.error ? errorState : initialErrorState,
      isCalendarVisible: false,
      monthArr: initialMonthArr,
      selectedRange: {
        endDate: (value === null || value === void 0 ? void 0 : (_value$endDate = value.endDate) === null || _value$endDate === void 0 ? void 0 : _value$endDate.length) > 0 ? (_value$endDate2 = value.endDate) === null || _value$endDate2 === void 0 ? void 0 : _value$endDate2.trim() : null,
        startDate: (value === null || value === void 0 ? void 0 : (_value$startDate = value.startDate) === null || _value$startDate === void 0 ? void 0 : _value$startDate.length) > 0 ? (_value$startDate2 = value.startDate) === null || _value$startDate2 === void 0 ? void 0 : _value$startDate2.trim() : null
      },
      yearRange: yearRange
    };
  }
  return {
    activeMonth: '',
    disableDates: disableDates,
    internalError: initialErrorState,
    isCalendarVisible: false,
    monthArr: initialMonthArr,
    selectedDate: (value === null || value === void 0 ? void 0 : value.length) > 0 ? value.trim() : null
  };
};
function GregorianCalendar(_ref2) {
  var autoOk = _ref2.autoOk;
    _ref2.className;
    var dataTestId = _ref2['data-testid'],
    disabled = _ref2.disabled,
    disableDates = _ref2.disableDates,
    error = _ref2.error,
    errorText = _ref2.errorText,
    _ref2$format = _ref2.format,
    format = _ref2$format === void 0 ? 'MM/DD/YYYY' : _ref2$format,
    highlight = _ref2.highlight,
    hintText = _ref2.hintText,
    id = _ref2.id,
    noValidate = _ref2.noValidate,
    limitRange = _ref2.limitRange;
    _ref2.location;
    _ref2.onChange;
    var onError = _ref2.onError,
    onUpdate = _ref2.onUpdate,
    quickSelectRange = _ref2.quickSelectRange,
    required = _ref2.required,
    type = _ref2.type,
    yearRange = _ref2.yearRange,
    value = _ref2.value,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var escPress = useKeyPress(27);
  var externalError = {
    error: error,
    errorText: errorText
  };
  var isGregorianRange = type === 'gregorian-range';
  var hasMountedRef = useRef(false);
  var _useReducer = useReducer(reducer, {
      disableDates: disableDates,
      format: format,
      isGregorianRange: isGregorianRange,
      value: value,
      yearRange: yearRange
    }, createInitialState),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    state = _useReducer2[0],
    dispatch = _useReducer2[1];
  var valueAsJSON = JSON.stringify(value);
  useEffect(function () {
    isGregorianRange ? dispatch(setSelectedRange(value, format)) : dispatch(setSelectedDate(value !== null && value !== void 0 && value.length ? value.trim() : null, format));
    //eslint-disable-next-line
    // useEffect doesn't like the nested attributes from value(data range), JSON.stringfy will treat it as a string and flatten the nested structure
  }, [valueAsJSON, format]);
  var selectedRangeAsJSON = JSON.stringify(state.selectedRange);
  useEffect(function () {
    onError && onError(state.internalError.error, isGregorianRange ? state.selectedRange : state.selectedDate);
    if (state.internalError.error === false && state.selectedDate !== null && state.selectedRange !== null) {
      hasMountedRef.current === true && onUpdate && onUpdate(id, isGregorianRange ? state.selectedRange : state.selectedDate);
    }

    // Setting the hasMountedRef via ref so it does not fire a rerender
    hasMountedRef.current = true;

    // eslint-disable-next-line
  }, [state.internalError.error, state.selectedDate, selectedRangeAsJSON, id]);
  useEffect(function () {
    dispatch(setDisabledDates(disableDates));
  }, [disableDates]);
  if (escPress && state.isCalendarVisible) {
    dispatch(hideCalendar());
  }
  var selectMonth = function selectMonth(type, monthSelect) {
    dispatch(setActiveMonth(toActiveMonth(state.activeMonth ? state.activeMonth : state.selectedDate, type, monthSelect)), format);
  };
  var selectDate = function selectDate(type) {
    isGregorianRange ? dispatch(setSelectedRange(value, format)) : dispatch(setSelectedDate(handleDateTransition(state.selectedDate, type, disableDates, format), format));
    dispatch(hideCalendar());
  };
  var changeEvent = function changeEvent(event) {
    isGregorianRange ? dispatch(setSelectedRange(event.target.value), format) : dispatch(setSelectedDate(getDate(event.target.value), format));
  };
  if (disableDates !== null && disableDates !== void 0 && disableDates.except && (disableDates.only || disableDates.weekdays || disableDates.weekends || disableDates.past || disableDates.future || disableDates.before || disableDates.after)) {
    throw new Error('Your datepicker disabled date rules have a conflict. "Except" cannot be combined with other rules.');
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Overlay, {
    isVisible: state.isCalendarVisible,
    onClick: function onClick() {
      return dispatch(hideCalendar());
    }
  }), /*#__PURE__*/React.createElement(InputField, _extends({}, otherProps, {
    id: id,
    changeEvent: changeEvent,
    required: required,
    "data-testid": dataTestId,
    disabled: !!disabled //not undefined only true/false
    ,
    format: format,
    hintText: hintText,
    highlight: highlight,
    internalError: noValidate ? false : state.internalError,
    externalError: noValidate ? false : externalError,
    disableDates: disableDates,
    selectDate: selectDate,
    selectedDate: state.selectedDate,
    selectedRange: state.selectedRange,
    isGregorianRange: isGregorianRange,
    isCalendarVisible: state.isCalendarVisible,
    setIsCalendarVisible: function setIsCalendarVisible(isVisible) {
      return dispatch(setCalendarVisibility(isVisible));
    },
    onClear: function onClear() {
      return isGregorianRange ? dispatch(setSelectedRange({})) : dispatch(setSelectedDate(''));
    }
  })), state.isCalendarVisible && !disabled && /*#__PURE__*/React.createElement(Card, {
    elevation: 2,
    className: "C-DatePicker__Card"
  }, /*#__PURE__*/React.createElement(Grid.Container, null, quickSelectRange ? /*#__PURE__*/React.createElement(Grid.Item, {
    className: "hc-pa-none"
  }, /*#__PURE__*/React.createElement(QuickRangeSelection, {
    quickSelectRange: quickSelectRange,
    setDate: function setDate(dateRange) {
      return dispatch(setSelectedRange(dateRange, format));
    },
    format: format,
    setIsCalendarVisible: function setIsCalendarVisible(isVisible) {
      return dispatch(setCalendarVisibility(isVisible));
    },
    disableDates: disableDates,
    yearRange: state.yearRange
  })) : null, /*#__PURE__*/React.createElement(Grid.Item, null, /*#__PURE__*/React.createElement(NavHeader, {
    activeMonth: state.activeMonth,
    selectMonth: selectMonth,
    yearRange: yearRange
  }), /*#__PURE__*/React.createElement(CalendarDates, {
    autoOk: autoOk,
    disableDates: state.disableDates,
    format: format,
    isGregorianRange: isGregorianRange,
    limitRange: limitRange,
    monthArr: state.monthArr,
    selectedDate: state.selectedDate,
    selectedRange: state.selectedRange,
    setSelectedRange: true,
    setDate: isGregorianRange ? function (dateRange) {
      dispatch(setSelectedRange(dateRange, format));
    } : function (date) {
      dispatch(setSelectedDate(date, format));
    },
    setIsCalendarVisible: function setIsCalendarVisible(isVisible) {
      return dispatch(setCalendarVisibility(isVisible));
    }
  })))));
}
var DatePicker = GregorianCalendar;

export { DatePicker as default };
